/* eslint-disable no-useless-catch */
//CandidateDetailsModal.js

import React, { useState, Fragment, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  collection,
  addDoc,
  getDoc,
} from "firebase/firestore";
import { db, Timestamp } from "../firebase-config";
import { useAuth } from "../useAuth";
import Modal from "../components/Modal";
import Accordion from "../components/Accordion";
import ScoreRing from "../components/common/ScoreRing";
import { useTranslation } from "react-i18next";
import { closeCandidateDetailsModal } from "../utils/redux/candidateSlice";
import JobPostingDescription from "../components/JobPostingDescription";
import EmailModal from "../components/EmailModal";
import NotesModal from "../components/NotesModal";
import TagModal from "../components/TagModal";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faEnvelope,
  faPlus,
  faStickyNote,
  faTag,
  faUser,
  faTimesCircle,
  faTimes,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import i18next from "i18next";
import CandidateScoreReason from "./CandidateScoreReason";

/**
 * Extracts initials from a full name string.
 */
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getStatusStyle = (status) => {
  switch (status) {
    case "Offered":
    case "Offer":
      return "text-emerald-700 bg-emerald-50 border border-emerald-200";
    case "Rejected":
    case "Rejeter":
      return "text-red-700 bg-red-50 border border-red-200";
    case "Interview":
      return "text-blue-700 bg-blue-50 border border-blue-200";
    case "Evaluation":
      return "text-amber-700 bg-amber-50 border border-amber-200";
    default:
      return "text-slate-600 bg-slate-100 border border-slate-200";
  }
};

const CandidateDetailsModal = () => {
  const { t } = useTranslation("CandidateDetailsModal");
  const dispatch = useDispatch();
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18next.language);
  const { currentUser } = useAuth();

  useEffect(() => {
    const browserLanguage = navigator.language || navigator.userLanguage;
    const languageCode = browserLanguage.split("-")[0];
    if (
      languageCode !== i18next.language &&
      (languageCode === "en" || languageCode === "fr")
    ) {
      i18next.changeLanguage(languageCode);
      setCurrentLanguage(languageCode);
    }
  }, []);

  const handleCloseModal = () => {
    dispatch(closeCandidateDetailsModal());
  };

  const isCandidateDetailsModalOpen = useSelector(
    (state) => state.candidates.isCandidateDetailsModalOpen
  );
  const selectedCandidate = useSelector(
    (state) => state.candidates.selectedCandidate
  );
  if (!isCandidateDetailsModalOpen || !selectedCandidate) {
    return null;
  }

  const candidate = selectedCandidate;
  const totalScore = candidate.score_list?.total_score ?? 0;
  const isRejected = ["Rejeter", "Rejected"].includes(candidate.ApplicationStatus);

  const handleSendEmail = async (subject, body) => {
    try {
      const emailData = {
        to: candidate.ContactInformation.Email,
        message: { subject: subject, text: body },
        createdAt: Timestamp.now(),
      };
      const emailRef = collection(db, "send-grid-emails");
      await addDoc(emailRef, emailData);
      setIsEmailModalOpen(false);
    } catch (error) {
      throw error;
    }
  };

  const handleAddNote = async (noteText) => {
    const candidateRef = doc(
      db,
      `/job-descriptions/${candidate.tenantId}/active/${candidate.jobUuid}/applications`,
      candidate.applicantId
    );
    try {
      await updateDoc(candidateRef, {
        notes: arrayUnion({
          text: noteText,
          authorId: currentUser.email,
          authorName: currentUser.displayName,
          timestamp: Timestamp.now(),
        }),
      });
      setIsNotesModalOpen(false);
    } catch (error) {
      throw error;
    }
  };

  const handleAddTag = async (tagText) => {
    const candidateRef = doc(
      db,
      `/job-descriptions/${candidate.tenantId}/active/${candidate.jobUuid}/applications`,
      candidate.applicantId
    );
    try {
      const docSnap = await getDoc(candidateRef);
      if (docSnap.exists()) {
        const existingTags = docSnap.data().tags || [];
        if (existingTags.includes(tagText)) {
          return;
        }
      }
      await updateDoc(candidateRef, {
        tags: arrayUnion(tagText),
      });
      setIsTagModalOpen(false);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteTag = async (tagToDelete) => {
    const candidateRef = doc(
      db,
      `/job-descriptions/${candidate.tenantId}/active/${candidate.jobUuid}/applications`,
      candidate.applicantId
    );
    try {
      const docSnap = await getDoc(candidateRef);
      if (docSnap.exists()) {
        const existingTags = docSnap.data().tags || [];
        const updatedTags = existingTags.filter((tag) => tag !== tagToDelete);
        await updateDoc(candidateRef, {
          tags: updatedTags,
        });
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <Modal isOpen={isCandidateDetailsModalOpen} onClose={handleCloseModal} size="xl">
      <EmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSend={handleSendEmail}
        candidateEmail={
          candidate.ContactInformation ? candidate.ContactInformation.Email : ""
        }
      />
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        onAddNote={handleAddNote}
        candidateId={candidate.applicantId}
        tenantId={candidate.tenantId}
      />
      <TagModal
        isOpen={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        onAddTag={handleAddTag}
        candidateId={candidate.applicantId}
        tenantId={candidate.tenantId}
      />

      <div className="space-y-4">
        {/* ── Header ── */}
        <div className="flex items-start justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div
              className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold tracking-wide ${
                isRejected
                  ? "bg-red-100 text-red-700"
                  : "bg-[#0077B6]/10 text-[#0077B6]"
              }`}
            >
              {getInitials(candidate.FullName)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                {candidate.FullName}
              </h2>
              <span
                className={`inline-block mt-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${getStatusStyle(
                  candidate.ApplicationStatus
                )}`}
              >
                {candidate.ApplicationStatus}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsEmailModalOpen(true)}
              className="w-9 h-9 rounded-full bg-[#A47713] hover:bg-[#8a6310] text-white flex items-center justify-center transition-colors"
              title={t("candidateDetailsModal.sendEmail")}
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-sm" />
            </button>
            <button
              onClick={() => setIsNotesModalOpen(true)}
              className="w-9 h-9 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-colors"
              title={t("candidateDetailsModal.addNote")}
            >
              <FontAwesomeIcon icon={faStickyNote} className="text-sm" />
            </button>
            <button
              onClick={() => setIsTagModalOpen(true)}
              className="w-9 h-9 rounded-full bg-amber-500 hover:bg-amber-600 text-white flex items-center justify-center transition-colors"
              title={t("candidateDetailsModal.addTag")}
            >
              <FontAwesomeIcon icon={faTag} className="text-sm" />
            </button>
          </div>
        </div>

        {/* ── Score Overview ── */}
        <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100">
          <div className="flex items-center gap-5">
            <ScoreRing score={totalScore} size={72} strokeWidth={5} />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-800 mb-1">
                {t("candidateDetailsModal.score.ranking")}: {totalScore}/100
              </p>
              <p className="text-xs text-slate-500">
                {t("candidateDetailsModal.score.experience")}: 60, {t("candidateDetailsModal.score.education")}: 20
              </p>
              <div className="mt-2 text-xs text-slate-600">
                <JobPostingDescription
                  description={candidate.score_list?.summary?.[i18next.languages[0]]}
                />
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200">
            <CandidateScoreReason description={candidate.score_list} />
          </div>
        </div>

        {/* ── Accordion Sections ── */}
        <div className="space-y-3">
          <Accordion title={t("candidateDetailsModal.contactInfo.title")} defaultOpen={true}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.fullName")}:</strong>{" "}
                  {candidate.FullName}
                </p>
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.email")}:</strong>{" "}
                  {candidate.ContactInformation?.Email ?? "—"}
                </p>
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.phone")}:</strong>{" "}
                  {candidate.ContactInformation?.Phone ?? "—"}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.address")}:</strong>{" "}
                  {candidate.ContactInformation?.Address ?? "—"}
                </p>
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.city")}:</strong>{" "}
                  {candidate.ContactInformation?.City ?? "—"}
                </p>
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.state")}:</strong>{" "}
                  {candidate.ContactInformation?.State ?? "—"}
                </p>
                <p className="text-slate-600">
                  <strong className="text-slate-800">{t("candidateDetailsModal.contactInfo.country")}:</strong>{" "}
                  {candidate.ContactInformation?.Country?.name ?? "—"}
                </p>
              </div>
            </div>
          </Accordion>

          {candidate.assessmentNames && candidate.assessmentNames.length > 0 && (
            <Accordion title={t("candidateDetailsModal.assessments.title")}>
              <div className="space-y-3">
                {candidate.assessmentNames.map((assessment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100"
                  >
                    <div className="flex items-center gap-3">
                      <ScoreRing score={assessment.score} size={36} strokeWidth={3} />
                      <div>
                        <p className="font-medium text-slate-800">{assessment.title}</p>
                        <p className="text-xs text-slate-500">{assessment.score}/100</p>
                      </div>
                    </div>
                    <a
                      href={`${process.env.REACT_APP_BASE_URL}assessmentresults/${assessment.link}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-[#0077B6] hover:underline flex items-center gap-1"
                    >
                      {t("candidateDetailsModal.assessments.viewResults")}
                      <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
                    </a>
                  </div>
                ))}
              </div>
            </Accordion>
          )}

          <Accordion title={t("candidateDetailsModal.documents.title")}>
            <div className="flex flex-wrap gap-2">
              {candidate.Resume && (
                <a
                  href={candidate.resumeURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#A47713]/10 text-[#A47713] rounded-full text-xs font-medium hover:bg-[#A47713]/20 transition-colors border border-[#A47713]/20"
                >
                  <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                  {t("candidateDetailsModal.documents.resume")}
                </a>
              )}
              {candidate.CoverLetter && (
                <a
                  href={candidate.coverLetterURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077B6]/10 text-[#0077B6] rounded-full text-xs font-medium hover:bg-[#0077B6]/20 transition-colors border border-[#0077B6]/20"
                >
                  <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                  {t("candidateDetailsModal.documents.coverLetter")}
                </a>
              )}
              {candidate.Certificates && candidate.certificationURL && (
                <a
                  href={candidate.certificationURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium hover:bg-emerald-100 transition-colors border border-emerald-200"
                >
                  <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                  {t("candidateDetailsModal.documents.certificates")}
                </a>
              )}
              {candidate.Diplomas && candidate.diplomaURL && (
                <a
                  href={candidate.diplomaURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-xs font-medium hover:bg-purple-100 transition-colors border border-purple-200"
                >
                  <FontAwesomeIcon icon={faDownload} className="text-[10px]" />
                  {t("candidateDetailsModal.documents.diplomas")}
                </a>
              )}
              {!candidate.Resume && !candidate.CoverLetter && !candidate.Certificates && !candidate.Diplomas && (
                <p className="text-sm text-slate-500">{t("candidateDetailsModal.documents.notAvailable")}</p>
              )}
            </div>
          </Accordion>

          <Accordion title={t("candidateDetailsModal.education.title")}>
            {candidate.Education && candidate.Education.length > 0 ? (
              <div className="space-y-3">
                {candidate.Education.map((edu, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="font-medium text-slate-800">
                      {edu.Degree} {t("candidateDetailsModal.education.in")} {edu.FieldOfStudy}
                    </p>
                    <p className="text-sm text-slate-500">
                      {edu.Institution} ({edu.GraduationYear})
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.education.noEducation")}</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.workExperience.title")}>
            {candidate.WorkExperience && candidate.WorkExperience.length > 0 ? (
              <div className="space-y-3">
                {candidate.WorkExperience.map((exp, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="font-medium text-slate-800">{exp.Role}</p>
                    <p className="text-sm text-slate-500">
                      {exp.CompanyName} · {exp.StartDate} – {exp.EndDate}
                    </p>
                    {exp.Responsibilities && (
                      <p className="mt-1 text-sm text-slate-600">{exp.Responsibilities}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.workExperience.noExperience")}</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.skills.title")}>
            {candidate.Skills ? (
              <div className="flex flex-wrap gap-2">
                {(typeof candidate.Skills === "string"
                  ? candidate.Skills.split(",")
                  : Array.isArray(candidate.Skills)
                  ? candidate.Skills
                  : []
                ).map((skill, idx) => (
                  <span
                    key={idx}
                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-[#0077B6]/10 text-[#0077B6] border border-[#0077B6]/20"
                  >
                    {typeof skill === "string" ? skill.trim() : skill}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">—</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.certifications.title")}>
            {candidate.Certifications && candidate.Certifications.length > 0 ? (
              <div className="space-y-3">
                {candidate.Certifications.map((cert, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="font-medium text-slate-800">{cert.CertificationName}</p>
                    <p className="text-sm text-slate-500">
                      {cert.IssuingOrganization} · {cert.DateObtained}
                      {cert.ExpiryDate ? ` – ${cert.ExpiryDate}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.certifications.none")}</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.languages.title")}>
            {candidate.Languages && candidate.Languages.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {candidate.Languages.map((lang, index) => (
                  <span
                    key={index}
                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200"
                  >
                    {lang.Language} — {lang.Proficiency}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.languages.none")}</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.references.title")}>
            {candidate.References && candidate.References.length > 0 ? (
              <div className="space-y-3">
                {candidate.References.map((ref, index) => (
                  <div key={index} className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                    <p className="font-medium text-slate-800">{ref.RefereeName}</p>
                    <p className="text-sm text-slate-500">
                      {ref.Relation} · {ref.ContactInformation}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.references.none")}</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.personalStatement")}>
            <p className="text-slate-600 leading-relaxed">{candidate.PersonalStatement || "—"}</p>
          </Accordion>

          <Accordion title={t("candidateDetailsModal.socialMedia.title")}>
            <div className="flex flex-wrap gap-2">
              {candidate.SocialMediaProfiles?.LinkedIn && (
                <a
                  href={candidate.SocialMediaProfiles.LinkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
                  LinkedIn
                </a>
              )}
              {candidate.SocialMediaProfiles?.Behance && (
                <a
                  href={candidate.SocialMediaProfiles.Behance}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-full text-xs font-medium hover:bg-purple-100 transition-colors border border-purple-200"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
                  Behance
                </a>
              )}
              {!candidate.SocialMediaProfiles?.LinkedIn && !candidate.SocialMediaProfiles?.Behance && (
                <p className="text-sm text-slate-500">—</p>
              )}
            </div>
          </Accordion>

          <Accordion title={t("candidateDetailsModal.portfolio")}>
            {candidate.Portfolio ? (
              <a
                href={candidate.Portfolio}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#A47713]/10 text-[#A47713] rounded-full text-xs font-medium hover:bg-[#A47713]/20 transition-colors border border-[#A47713]/20"
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-[10px]" />
                {t("candidateDetailsModal.viewPortfolio")}
              </a>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.noPortfolio")}</p>
            )}
          </Accordion>

          <Accordion title={t("candidateDetailsModal.volunteerExperience")}>
            <p className="text-slate-600 leading-relaxed">{candidate.VolunteerExperience || "—"}</p>
          </Accordion>

          <Accordion title={t("candidateDetailsModal.availability")}>
            <p className="text-slate-600 leading-relaxed">{candidate.Availability || "—"}</p>
          </Accordion>

          {/* Notes */}
          <Accordion title={t("candidateDetailsModal.notes.title")}>
            {candidate.notes && candidate.notes.length > 0 ? (
              <div className="space-y-3">
                {candidate.notes.map((note, index) => (
                  <div
                    key={index}
                    className="p-3 bg-slate-50 rounded-xl border border-slate-100"
                  >
                    <p className="text-sm text-slate-700">{note.text}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-5 h-5 rounded-full bg-[#0077B6]/10 text-[#0077B6] flex items-center justify-center text-[9px] font-bold">
                        {note.authorName?.[0]?.toUpperCase() ?? "?"}
                      </div>
                      <p className="text-xs text-slate-400">
                        {note.authorName} ·{" "}
                        {note.timestamp?._seconds
                          ? new Date(note.timestamp._seconds * 1000).toLocaleDateString(
                              undefined,
                              { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
                            )
                          : "—"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500">{t("candidateDetailsModal.notes.noNotes")}</p>
            )}
          </Accordion>

          {/* Tags */}
          <Accordion title={t("candidateDetailsModal.tags.title")}>
            <div className="flex flex-wrap gap-2">
              {candidate.tags && candidate.tags.length > 0 ? (
                candidate.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-[#A47713]/10 text-[#A47713] border border-[#A47713]/20 group"
                  >
                    {tag}
                    <button
                      onClick={() => handleDeleteTag(tag)}
                      className="w-4 h-4 rounded-full bg-[#A47713]/20 hover:bg-red-500 hover:text-white text-[#A47713] flex items-center justify-center transition-colors"
                      title="Delete tag"
                    >
                      <FontAwesomeIcon icon={faTimes} className="text-[8px]" />
                    </button>
                  </span>
                ))
              ) : (
                <p className="text-slate-500">{t("candidateDetailsModal.tags.noTags")}</p>
              )}
            </div>
          </Accordion>
        </div>
      </div>
    </Modal>
  );
};

export default CandidateDetailsModal;
